/**
 * URL Normalization Utility
 *
 * Optionally redirects between apex and www for drheranca.com.br so the browser
 * origin matches API CORS. Mode: REACT_APP_CANONICAL_HOST (apex | www | none).
 */

/**
 * Which hostname is canonical for drheranca.com.br (also set REACT_APP_PUBLIC_URL to match).
 * - apex: redirect www → drheranca.com.br (default; aligns with GSC property on apex)
 * - www: redirect apex → www.drheranca.com.br (use when Search Console uses the www property)
 * - none: no redirect; API CORS must allow both origins
 */
type CanonicalHostMode = 'apex' | 'www' | 'none';

function getCanonicalHostMode(): CanonicalHostMode {
  const raw = process.env.REACT_APP_CANONICAL_HOST?.trim().toLowerCase();
  if (raw === 'www' || raw === 'none') {
    return raw;
  }
  return 'apex';
}

const CANONICAL_HOST_MODE: CanonicalHostMode = getCanonicalHostMode();

/**
 * Domains that should be normalized
 * Only domains in this list will trigger redirects
 */
const DOMAINS_TO_NORMALIZE = [
  'drheranca.com.br',
];

/**
 * Checks if the current hostname should be normalized
 */
function shouldNormalizeHostname(hostname: string): boolean {
  return DOMAINS_TO_NORMALIZE.some(domain => 
    hostname === domain || hostname === `www.${domain}`
  );
}

/**
 * Normalizes the hostname (removes or adds www based on configuration)
 */
function normalizeHostname(hostname: string): string | null {
  if (!shouldNormalizeHostname(hostname)) {
    return null;
  }

  if (CANONICAL_HOST_MODE === 'none') {
    return null;
  }

  const hasWww = hostname.startsWith('www.');

  if (CANONICAL_HOST_MODE === 'apex' && hasWww) {
    return hostname.replace(/^www\./, '');
  }
  if (CANONICAL_HOST_MODE === 'www' && !hasWww) {
    return `www.${hostname}`;
  }

  return null;
}

/**
 * Performs URL normalization if needed
 * Redirects the browser if the current URL should be normalized
 * 
 * @returns true if a redirect was performed, false otherwise
 */
export function normalizeUrlIfNeeded(): boolean {
  // Only run in browser environment
  if (typeof window === 'undefined') {
    return false;
  }

  const currentHostname = window.location.hostname;
  const normalizedHostname = normalizeHostname(currentHostname);

  if (normalizedHostname && normalizedHostname !== currentHostname) {
    // Build the new URL with normalized hostname
    const newUrl = `${window.location.protocol}//${normalizedHostname}${window.location.pathname}${window.location.search}${window.location.hash}`;
    
    // Perform redirect
    window.location.replace(newUrl);
    return true;
  }

  return false;
}

/**
 * Registered domain without www (e.g. api host comparisons).
 */
export function getCanonicalDomain(hostname: string): string {
  return hostname.replace(/^www\./, '');
}

/**
 * True when the current hostname matches REACT_APP_CANONICAL_HOST for this site.
 */
export function isCanonicalOrigin(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  const hostname = window.location.hostname;
  if (CANONICAL_HOST_MODE === 'none') {
    return true;
  }
  if (CANONICAL_HOST_MODE === 'apex') {
    return hostname === getCanonicalDomain(hostname);
  }
  return DOMAINS_TO_NORMALIZE.some((domain) => hostname === `www.${domain}`);
}





